import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ApiError,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  StatusChip,
  Switch,
} from '@smena/ui';
import { QueryState } from '@/components/QueryState';
import {
  fetchTelegramSettings,
  saveTelegramSettings,
  type SaveTelegramInput,
  type TelegramSettingsView,
} from '@/features/telegram/api';

/**
 * Карточка «Telegram» на странице «Система»: тумблер прокси и адрес.
 *
 * Раньше это поле жило в панели УК - неверно по существу. Telegram заблокирован в РФ,
 * прокси - условие работы платформы, а не предпочтение клиента; адрес выдаёт вендор,
 * один на инсталляцию.
 *
 * Адрес с сервера не приходит (в URL логин и пароль), поэтому поле всегда пустое, а пустое
 * поле означает «не менять». Иначе включить тумблер и нажать «Сохранить» стирало бы
 * рабочий прокси - самый обидный способ положить все Telegram-каналы разом.
 */
export function TelegramProxyCard() {
  const q = useQuery({
    queryKey: ['telegram-settings'],
    queryFn: fetchTelegramSettings,
    staleTime: 5_000,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Telegram</CardTitle>
        <CardDescription>
          SOCKS5-прокси для всех Telegram-каналов. Из РФ Telegram недоступен напрямую, поэтому
          без прокси не поднимутся ни гостевой бот Smena, ни личные аккаунты УК. Настройка
          общая на всю инсталляцию: адрес выдаёт вендор, клиент его не видит и не меняет.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <QueryState isLoading={q.isLoading} error={q.error}>
          {/* key по updatedAt: новый снимок с сервера пересоздаёт форму и сбрасывает локальное
              состояние. Иначе тумблер продолжал бы показывать то, что человек кликнул до того,
              как настройку поменял кто-то другой. */}
          {q.data ? <ProxyForm key={q.data.updatedAt ?? 'empty'} data={q.data} /> : null}
        </QueryState>
      </CardContent>
    </Card>
  );
}

function ProxyForm({ data }: { data: TelegramSettingsView }) {
  const qc = useQueryClient();
  // Тумблер держим в локальном состоянии: он часть той же формы, что и адрес, - включить
  // прокси и задать адрес это одно действие, а не два запроса подряд.
  const [enabled, setEnabled] = useState(data.proxyEnabled);
  const [proxyUrl, setProxyUrl] = useState('');

  const save = useMutation({
    mutationFn: (input: SaveTelegramInput) => saveTelegramSettings(input),
    onSuccess: (view) => {
      toast.success(view.proxyEnabled ? 'Прокси включён' : 'Прокси выключен');
      setProxyUrl('');
      setEnabled(view.proxyEnabled);
      void qc.invalidateQueries({ queryKey: ['telegram-settings'] });
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : 'Не удалось сохранить');
      // Возвращаем тумблер к сохранённому состоянию: зелёный переключатель при отклонённом
      // сохранении - это ложь о том, работает ли канал.
      setEnabled(data.proxyEnabled);
    },
  });

  function submit(e: FormEvent): void {
    e.preventDefault();
    save.mutate({
      proxyEnabled: enabled,
      ...(proxyUrl.trim() ? { proxyUrl: proxyUrl.trim() } : {}),
    });
  }

  const toggleDirty = enabled !== data.proxyEnabled;
  const dirty = toggleDirty || proxyUrl.trim() !== '';

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <Label htmlFor="tg-proxy-enabled">Использовать прокси</Label>
          <span className="text-xs text-muted">
            Выключение не стирает адрес - трафик просто идёт напрямую.
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Чип показывает сохранённое состояние, тумблер - намерение. Пока они расходятся,
              чип честно говорит «не сохранено»: зелёный рядом с несохранённым тумблером
              означал бы, что канал уже работает через прокси. */}
          <StatusChip tone={toggleDirty ? 'muted' : data.proxyEnabled ? 'green' : 'amber'}>
            {toggleDirty ? 'не сохранено' : data.proxyEnabled ? 'включён' : 'выключен'}
          </StatusChip>
          <Switch
            id="tg-proxy-enabled"
            checked={enabled}
            onChange={setEnabled}
            disabled={save.isPending}
            aria-label="Использовать прокси для Telegram"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5 border-t border-line pt-4">
        <Label htmlFor="tg-proxy-url">Адрес прокси</Label>
        <Input
          id="tg-proxy-url"
          type="password"
          autoComplete="new-password"
          value={proxyUrl}
          onChange={(e) => setProxyUrl(e.target.value)}
          placeholder={data.hasProxyUrl ? 'не менять' : 'socks5://user:pass@host:1080'}
          disabled={save.isPending}
        />
        <p className="text-xs text-muted">
          {data.hasProxyUrl ? (
            <>
              Сейчас: <code className="font-mono">{data.proxyLabel ?? 'адрес задан'}</code>. Пароль
              наружу не отдаётся, поэтому адрес нельзя отредактировать - только ввести заново.
              Пустое поле оставляет сохранённый.
            </>
          ) : (
            <>Адрес не задан. Формат socks5://user:pass@host:port, HTTP-прокси не подходит.</>
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-line pt-4">
        <Button type="submit" disabled={save.isPending || !dirty}>
          {save.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button>
        {data.hasProxyUrl ? (
          <Button
            type="button"
            variant="outline"
            disabled={save.isPending}
            onClick={() => {
              // Явное удаление - отдельное действие с null: пустое поле «стереть» не значит.
              // Спрашиваем подтверждение: адрес общий на инсталляцию, и без него встают
              // Telegram-каналы всех клиентов разом.
              if (!confirm('Удалить адрес прокси? Telegram-каналы всех аккаунтов перестанут работать, пока не задан новый.')) return;
              setEnabled(false);
              save.mutate({ proxyEnabled: false, proxyUrl: null });
            }}
          >
            Удалить адрес
          </Button>
        ) : null}
        <span className="text-xs text-muted">
          {data.updatedAt
            ? `Изменено ${new Date(data.updatedAt).toLocaleString('ru-RU')}${
                data.updatedByEmail ? ` · ${data.updatedByEmail}` : ''
              }`
            : 'В админке ещё не сохранялось - действует TG_PROXY_URL из .env стенда'}
        </span>
      </div>
    </form>
  );
}
